import axios from "axios";
import FileSaver from "file-saver";
import { notification } from "antd";

import history from "../history";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN } from "redux/constants/Auth";
import MenuService, {
	SELECTED_TENANT_STORAGE_KEY as TENANT_STORAGE_KEY,
} from "services/MenuService";

function onFulfilledRequest(config) {
	const jwtToken = localStorage.getItem(AUTH_TOKEN);

	if (jwtToken) {
		config.headers[TOKEN_PAYLOAD_KEY] = jwtToken;
	}

	const tenantSchema = localStorage.getItem(TENANT_STORAGE_KEY);
	const isPublicRequest = Boolean(config.headers[PUBLIC_REQUEST_KEY]);
	const tenantOverride = config.headers["x-tenant-override"];

	if (tenantOverride) {
		config.headers["x-tenant"] = tenantOverride;
		delete config.headers["x-tenant-override"];
	} else if (tenantSchema && !isPublicRequest) {
		config.headers["x-tenant"] = tenantSchema;
	} else {
		delete config.headers["x-tenant"];
	}

	if (!jwtToken && !config.headers[PUBLIC_REQUEST_KEY]) {
		history.push(ENTRY_ROUTE);
		window.location.reload();
	}

	return config;
}

function handleRequestError(error) {
	// Do something with request error here
	notification.error({
		message: "Error",
	});
	Promise.reject(error);
}

function isTenantAccessError(error) {
	const status = error?.response?.status;
	const message = String(error?.response?.data?.message || "").toLowerCase();
	return (
		(status === 400 || status === 403) &&
		(message.includes("tenant") || message.includes("empresa"))
	);
}

async function handleResponseError(error) {
	if (!error.response) {
		notification.error({ message: "Erro inesperado" });
		return Promise.reject(error);
	}

	if (isTenantAccessError(error) && localStorage.getItem(AUTH_TOKEN)) {
		const handled = await MenuService.handleTenantAccessError();
		if (handled) {
			return Promise.reject(error);
		}
	}

	let notificationParam = {
		message: "",
	};

	// Remove token and redirect
	if (error.response.status === 400) {
		notificationParam.message =
			error.response.data?.message || "Erro inesperado";
	}

	if (error.response.status === 401) {
		notificationParam.message = "Falha na autenticação";
		notificationParam.description = error.message;
		localStorage.removeItem(AUTH_TOKEN);
		notification.error(notificationParam);
		return Promise.reject(error);
	}

	if (error.response.status === 404 || error.response.status === 403) {
		if (isTenantAccessError(error) && localStorage.getItem(AUTH_TOKEN)) {
			return Promise.reject(error);
		}
		if (error.response.data?.message) {
			notificationParam.message = error.response.data.message;
		} else if (error.response.data?.error) {
			notificationParam.message = error.response.data.error;
		} else {
			notificationParam.message = "Not Found";
		}
	}

	if (
		error.response.status === 400 &&
		isTenantAccessError(error) &&
		localStorage.getItem(AUTH_TOKEN)
	) {
		return Promise.reject(error);
	}

	if (error.response.status === 500) {
		if (error.response.data?.message) {
			notificationParam.message = error.response.data.message.toString();
		} else if (error.response.data?.error?.message) {
			notificationParam.message =
				error.response.data.error.message.toString();
		} else if (typeof error.response.data?.error == "string") {
			notificationParam.message = error.response.data.error.toString();
		} else {
			notificationParam.message = "Erro inesperado";
		}
	}

	if (error.response.status === 508) {
		notificationParam.message = "Time Out";
	}

	notification.error(notificationParam);

	return Promise.reject(error);
}

const ENTRY_ROUTE = "#/auth/login";
const PUBLIC_REQUEST_KEY = "public-request";
const TOKEN_PAYLOAD_KEY = "authorization";
const SELECTED_TENANT_STORAGE_KEY = "SELECTED_TENANT_SCHEMA";

const config = {
	baseURL: API_BASE_URL,
	timeout: 300000,
};

export const apiFetchClient = axios.create(config);

apiFetchClient.interceptors.request.use(onFulfilledRequest, handleRequestError);
apiFetchClient.interceptors.response.use((response) => {
	return response.data;
}, handleResponseError);

export const apiDownloadClient = axios.create({
	...config,
	responseType: "blob",
});

apiDownloadClient.interceptors.request.use(
	onFulfilledRequest,
	handleRequestError,
);
apiDownloadClient.interceptors.response.use((response) => {
	const filename = response.headers["content-disposition"]
		.split(";")[1]
		.split("=")[1];
	FileSaver.saveAs(response.data, filename);

	return response;
}, handleResponseError);

export default apiFetchClient;
